import { isSavedAnalysis } from '@modules/analysis/helpers';
import { useAddAnalysisToDashboard } from '@modules/analysis/hooks/useCreateAnalysis/useAddAnalysisToDashboard';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_6S } from '@modules/common/constants';
import { Direction } from '@modules/common/types';
import { getAnalysisTypeDisplayName } from '@modules/dashboard/helpers';
import { EditableDashboardListItem } from '@modules/dashboard/types';
import DashboardCustomizeRoundedIcon from '@mui/icons-material/DashboardCustomizeRounded';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { AnalysisViewFragment } from 'generated/graphql';
import { useState } from 'react';
import { CreateNewDashboardDialogContent } from '../CreateNewDashboardDialogContent';
import { OpenDashboardLink } from '../OpenDashboardLink';
import { AddAnalysisToDashboardMenu } from './AddAnalysisToDashboardMenu';

interface AddAnalysisToDashboardButtonProps {
  isDisabled?: boolean;
  renderAnalysisFormDialog: (
    params: SaveAnalysisAndAddToDashboardFormProps,
  ) => React.ReactNode;
}

export interface SaveAnalysisAndAddToDashboardFormProps {
  isOpen: boolean;
  dashboardToAddTo?: EditableDashboardListItem;
  shouldAddToNewDashboard?: boolean;
  onClose: () => void;
  shouldSkipRedirect?: boolean;
  onCreated?: (
    analysis?: Pick<AnalysisViewFragment, 'title' | 'viewToken' | 'useCase'>,
  ) => void;
}

export const AddAnalysisToDashboardButton = ({
  isDisabled = false,
  renderAnalysisFormDialog,
}: AddAnalysisToDashboardButtonProps) => {
  const [dashboardToAddTo, setDashboardToAddTo] =
    useState<EditableDashboardListItem>();
  const [isCreateNewDashbaordDialogOpen, setIsCreateNewDashboardDialogOpen] =
    useState(false);
  const [shouldAddToNewDashboard, setShouldAddToNewDashboard] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const useCase = useAnalysisStore(({ useCase }) => useCase);

  const analysisTypeDisplayName = getAnalysisTypeDisplayName(useCase);

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const analysisID = useAnalysisStore(({ id }) => id);

  const isSaved = isSavedAnalysis(analysisID);

  const [addAnalysisToDashboard, { loading: isUpdating }] =
    useAddAnalysisToDashboard();

  const { showSuccessNotification } = useNotificationContext();

  const addSavedAnalysis = async (dashboard: EditableDashboardListItem) => {
    await addAnalysisToDashboard({
      analysisID,
      dashboard,
      dashboardLayout: dashboard.metadataV2?.layout ?? { rows: [] },
      analysesMetadata: dashboard?.metadataV2?.analysesMetadata,
      shouldRefetchAnalyses: true,
      cellInsertDirection: Direction.Bottom,
    });
    showSuccessNotification({
      contentComponent: `This ${analysisTypeDisplayName} is added to "${dashboard.title}"`,
      props: {
        timeout: NOTIFICATION_TIMEOUT_LONG_6S,
        action: <OpenDashboardLink dashboard={dashboard} />,
      },
    });
  };

  const onDashboardSelect = (dashboard: EditableDashboardListItem) => {
    if (isSaved) {
      addSavedAnalysis(dashboard);
    } else {
      setDashboardToAddTo(dashboard);
    }
    closeMenu();
  };

  const onAddToNewDashboard = () => {
    if (isSaved) {
      setIsCreateNewDashboardDialogOpen(true);
    } else {
      setShouldAddToNewDashboard(true);
    }
    closeMenu();
  };

  const onSaveFormClose = () => {
    setDashboardToAddTo(undefined);
    setShouldAddToNewDashboard(false);
  };

  const onCreateNewDashboardDialogClose = () =>
    setIsCreateNewDashboardDialogOpen(false);

  return (
    <Tooltip
      title={`Add this ${analysisTypeDisplayName} to a Dashboard`}
      arrow
      placement="top"
      open={isTooltipOpen}
    >
      <Box>
        <IconButton
          color="primary"
          data-role="quantify-add-to-dashboard-button"
          onClick={openMenu}
          disabled={isUpdating || isDisabled}
          onMouseEnter={() => setIsTooltipOpen(true)}
          onMouseLeave={() => setIsTooltipOpen(false)}
        >
          {isUpdating ? (
            <Typography color="text.disabled">
              <CircularProgress size={24} color="inherit" />
            </Typography>
          ) : (
            <DashboardCustomizeRoundedIcon />
          )}
        </IconButton>
        <AddAnalysisToDashboardMenu
          menuAnchor={menuAnchor}
          onClose={closeMenu}
          onDashboardSelect={onDashboardSelect}
          onAddToNewDashboard={onAddToNewDashboard}
        />

        {renderAnalysisFormDialog({
          dashboardToAddTo,
          shouldAddToNewDashboard,
          isOpen: Boolean(dashboardToAddTo || shouldAddToNewDashboard),
          onClose: onSaveFormClose,
        })}

        <Dialog open={isCreateNewDashbaordDialogOpen} fullWidth maxWidth="md">
          <CreateNewDashboardDialogContent
            onClose={onCreateNewDashboardDialogClose}
          />
        </Dialog>
      </Box>
    </Tooltip>
  );
};
